/**
 * OG's worksheet tab order for an SDMS workbook — the move sequence its open path runs after the mail merge
 * (SpreadsheetEntryController GenerateMailMerge), one per template model:
 *
 *  - Model 1 (SampleSheetNavigation, :3845-3892): MoveToEnd, in this order, Sequence · Summary · Calibration ·
 *    ISTD · Parameter-Oriented Summary · Sample-Oriented Summary · Reagents · Instruments · Raw Data · Qualifier.
 *  - Model 2 (CalibrationSet, :4229-4252): Sequence → first (Summary → first when there is no Sequence), Summary →
 *    1, Regression Curves → 2, ISTD → 3, Raw Data → end.
 *  - Model 0 (single sheet, :4653-4670): Parameter-Oriented Summary · Sample-Oriented Summary · Raw Data → end.
 *
 * Qualifier ends last in every model (Model 0 / 2 add it after the moves — SetQualifierDropdown, :31774).
 * Every OG VOC blob holds this order; AI put Reagents BEFORE the two summaries (OG vs AI VOC comparison
 * 2026-09-28). The moves are idempotent, so re-applying them after any later step is safe.
 */
import type ExcelJS from 'exceljs';

const MODEL1_TO_END = ['Sequence', 'Summary', 'Calibration', 'ISTD', 'Parameter-Oriented Summary',
  'Sample-Oriented Summary', 'Reagents', 'Instruments', 'Raw Data', 'Qualifier'];
const MODEL0_TO_END = ['Parameter-Oriented Summary', 'Sample-Oriented Summary', 'Raw Data', 'Qualifier'];

/** `names` (the current tab order) re-ordered by OG's move sequence for `model`. Sheet names match
 *  case-insensitively, as DevExpress's `Worksheets[name]`; a sheet the workbook lacks is skipped. */
export function ogSheetOrder(names: readonly string[], model: number): string[] {
  const out = [...names];
  const find = (name: string) => out.findIndex(n => n.toUpperCase() === name.toUpperCase());
  const move = (name: string, at: number) => {
    const k = find(name); if (k < 0) return;
    const [n] = out.splice(k, 1);
    out.splice(Math.min(at, out.length), 0, n!);
  };
  if (model === 2) {
    if (find('Sequence') >= 0) { move('Sequence', 0); move('Summary', 1); }
    else move('Summary', 0);
    move('Regression Curves', 2);
    move('ISTD', 3);
    for (const n of ['Raw Data', 'Qualifier']) move(n, out.length);
  } else {
    for (const n of model === 1 ? MODEL1_TO_END : MODEL0_TO_END) move(n, out.length);
  }
  return out;
}

/** Applies {@link ogSheetOrder} to an ExcelJS workbook (ExcelJS honours `orderNo` on write, omits it from its typings). */
export function applyOgSheetOrder(wb: ExcelJS.Workbook, model: number): void {
  ogSheetOrder(wb.worksheets.map(w => w.name), model).forEach((name, i) => {
    const ws = wb.getWorksheet(name);
    if (ws) (ws as unknown as { orderNo: number }).orderNo = i;
  });
}
